import { useCallback, useEffect, useId, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ReactComponent as NotFoundIcon } from "assets/images/common/not-found.svg";
import { ReactComponent as PlusIcon } from "assets/images/common/plus.svg";

import useDebounce from "hooks/useDebounce";
import useRealtime from "hooks/useRealtime";

import { ModalType, UserRole } from "entities/common";
import { CreateCompanyRequest, UpdateCompanyRequest } from "entities/companies";

import PageTemplate from "modules/common/PageTemplate";

import { PAGES } from "utils/links";

import Button from "components/Button";
import Card from "components/Card";
import SearchInput from "components/SearchInput";
import Table from "components/Table";
import TableRowActions from "components/Table/TableRowActions";

import { useAppDispatch, useAppSelector } from "store/hooks";
import { showModal } from "store/slices/modalSlice";

import { RootStore } from "store";
import {
  changePage,
  createCompanyRequest,
  getCompanyListRequest,
  getCompanyListStatisticsRequest,
  resetCompanyList,
  resetSuccessfullyAddedCompany,
  resetSuccessfullyUpdatedCompany,
  updateCompanyRequest,
} from "store/slices/companiesSlice";
import { setModalCallbacks } from "store/slices/modalCallbacksRegistry";
import { showToast } from "store/slices/toastSlice";
import { QUERY_PARAMS } from "utils/constants";
import { COMPANY_COUNTRY } from "utils/data/emirates";
import { QueryHelper } from "utils/helpers";
import { CompanyFormValues } from "widgets/Modal/CreateCompany/Content";
import styles from "./style.module.scss";

type CompanyRowSource = {
  id: number;
  companyName: string;
  emirate: string;
  ownerName: string;
  ownerRole?: string;
  email: string;
  phone: string;
  compliance: string;
  complianceVariant: "error" | "warning" | "success" | "-";
  portfolios: string;
  assets: string;
  users: string;
  actions?: {
    view?: boolean;
    edit?: boolean;
    remove?: boolean;
  };
};

const CompanyListPage = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const callbackKey = useId();
  const {
    list,
    isLoading,
    successfullyAddedId,
    successfullyUpdatedId,
    successfullyRemovedId,
    meta,
    statistics,
    isStatisticLoading,
  } = useAppSelector((state: RootStore) => state.companies);
  const { user } = useAppSelector((state: RootStore) => state.auth);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebounce(search, 500);

  useEffect(() => {
    if (!!user?.role && user?.role !== UserRole.ADMINISTRATOR) {
      navigate(PAGES.FORBIDDEN);
    }
  }, [user?.role, navigate]);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    dispatch(changePage(1));
  };

  const handleViewCompany = useCallback(
    (id: number) => {
      navigate(`${PAGES.COMPANY_DETAIL.replace(":id", id.toString())}`);
    },
    [navigate],
  );

  const handleSaveCompany = useCallback(
    (values?: unknown) => {
      const data = values as CompanyFormValues;
      const body: CreateCompanyRequest = {
        company_name: data.companyName,
        country: COMPANY_COUNTRY,
        city: data.emirate,
        full_name: data.fullName,
        email: data.email,
        phone: data.phoneNumber,
        position: data.position,
      };

      dispatch(createCompanyRequest(body));
    },
    [dispatch],
  );

  const handleUpdateCompany = useCallback(
    (id: number) => (values?: unknown) => {
      const data = values as CompanyFormValues;
      const body: UpdateCompanyRequest = {
        company_name: data.companyName,
        country: COMPANY_COUNTRY,
        city: data.emirate,
      };

      dispatch(updateCompanyRequest({ id, body }));
    },
    [dispatch],
  );

  const handleCreateCompany = useCallback(() => {
    setModalCallbacks(callbackKey, { onSave: handleSaveCompany });

    dispatch(
      showModal({
        modalType: ModalType.CREATE_COMPANY,
        modalProps: { mode: "create", callbackKey },
      }),
    );
  }, [callbackKey, dispatch, handleSaveCompany]);

  const handleEditCompany = useCallback(
    (id: number) => {
      setModalCallbacks(callbackKey, { onSave: handleUpdateCompany(id) });

      const company = list.find((company) => company?.id === id);

      dispatch(
        showModal({
          modalType: ModalType.CREATE_COMPANY,
          modalProps: {
            mode: "edit",
            callbackKey,
            id,
            initialData: {
              companyName: company?.name || "",
              emirate: company?.city?.label || "",
            },
          },
        }),
      );
    },
    [callbackKey, handleUpdateCompany, list, dispatch],
  );

  const handleRemoveCompany = useCallback(
    (company: CompanyRowSource) => (id: number) => {
      dispatch(
        showModal({
          modalType: ModalType.REMOVE_COMPANY,
          modalProps: {
            id,
            name: company?.companyName || "",
            portfolios: company?.portfolios || "",
            assets: company?.assets || "",
            users: company?.users || "",
          },
        }),
      );
    },
    [dispatch],
  );

  const handlePageChange = useCallback(
    (page: number) => {
      dispatch(changePage(page));
    },
    [dispatch],
  );

  const companiesInfo = useMemo(
    () => [
      {
        title: "Total companies",
        value: statistics?.total_companies.toString() || "-",
      },
      {
        title: "Total portfolios",
        value: statistics?.total_portfolios.toString() || "-",
      },
      {
        title: "Total assets",
        value: statistics?.total_assets.toString() || "-",
      },
      {
        title: "Total users",
        value: statistics?.total_users.toString() || "-",
      },
    ],
    [statistics],
  );

  const tableHeaders = useMemo(
    () => [
      { label: "Company", key: "company" },
      { label: "Emirate", key: "emirate" },
      { label: "Owner", key: "owner" },
      { label: "Contact", key: "contact" },
      { label: "Compliance", key: "compliance" },
      { label: "Portfolios", key: "portfolios" },
      { label: "Assets", key: "assets" },
      { label: "Users", key: "users" },
      { label: "Actions", key: "actions" },
    ],
    [],
  );

  // TODO position
  const companies = useMemo<CompanyRowSource[]>(
    () =>
      list.map((company) => {
        const score = company?.compliance_score;
        const hasScore = typeof score === "number";

        return {
          id: company?.id,
          companyName: company?.name || "-",
          emirate: company?.city?.label || "-",
          ownerName: company?.owner?.full_name || "-",
          ownerRole: company?.owner?.position,
          email: company?.owner?.email || "-",
          phone: company?.owner?.phone || "-",
          compliance: hasScore ? `${score}%` : "-",
          complianceVariant: !hasScore ? "-" : score >= 90 ? "success" : score >= 70 ? "warning" : "error",
          portfolios: company?.portfolio_count?.toString() || "-",
          assets: company?.asset_count?.toString() || "-",
          users: company?.user_count?.toString() || "-",
        };
      }),
    [list],
  );

  const tableData = useMemo(
    () =>
      companies.map((row) => {
        const complianceClass =
          row.complianceVariant === "error"
            ? styles["risk-score-error"]
            : row.complianceVariant === "warning"
              ? styles["risk-score-warning"]
              : row.complianceVariant === "success"
                ? styles["risk-score-success"]
                : styles["risk-score-default"];

        return {
          id: row.id,
          company: <span className={styles.company}>{row.companyName}</span>,
          emirate: <span className={styles.emirate}>{row.emirate}</span>,
          owner: (
            <div className={styles.owner}>
              <span>{row.ownerName}</span>
              {row.ownerRole && <span>{row.ownerRole}</span>}
            </div>
          ),
          contact: (
            <div className={styles.contact}>
              <span>{row.email}</span>
              <span>{row.phone}</span>
            </div>
          ),
          compliance: <span className={complianceClass}>{row.compliance}</span>,
          portfolios: row.portfolios,
          assets: row.assets,
          users: row.users,
          actions: (
            <TableRowActions
              id={row.id}
              flags={row.actions}
              onView={handleViewCompany}
              onEdit={handleEditCompany}
              onRemove={handleRemoveCompany(row)}
            />
          ),
        };
      }),
    [companies, handleViewCompany, handleEditCompany, handleRemoveCompany],
  );

  // get statistics
  useEffect(() => {
    dispatch(getCompanyListStatisticsRequest());
  }, [dispatch]);

  // get company list
  useEffect(() => {
    dispatch(
      getCompanyListRequest(
        QueryHelper.getQueryString({ [QUERY_PARAMS.SEARCH]: debouncedSearch.trim(), page: meta.page || 1 }),
      ),
    );
  }, [dispatch, debouncedSearch, meta.page, successfullyAddedId, successfullyUpdatedId, successfullyRemovedId]);

  const reload = useCallback(() => {
    dispatch(
      getCompanyListRequest(
        QueryHelper.getQueryString({ [QUERY_PARAMS.SEARCH]: debouncedSearch.trim(), page: meta.page || 1 }),
        { silent: true },
      ),
    );
    dispatch(getCompanyListStatisticsRequest({ silent: true }));
  }, [dispatch, debouncedSearch, meta.page]);

  useRealtime(["compliance"], reload);

  // successfully added company
  useEffect(() => {
    if (successfullyAddedId) {
      dispatch(resetSuccessfullyAddedCompany());
      dispatch(
        showToast({
          id: "successfully-added-company",
          message: "The company was successfully created.",
          type: "success",
          duration: 3000,
        }),
      );
      navigate(PAGES.COMPANY_DETAIL.replace(":id", successfullyAddedId.toString()));
    }
  }, [successfullyAddedId, dispatch]);

  // successfully updated company
  useEffect(() => {
    if (successfullyUpdatedId) {
      dispatch(resetSuccessfullyUpdatedCompany());
      dispatch(
        showToast({
          id: "successfully-updated-company",
          message: "The company was successfully updated.",
          type: "success",
          duration: 3000,
        }),
      );
    }
  }, [successfullyUpdatedId, dispatch]);

  useEffect(() => {
    return () => {
      dispatch(resetCompanyList());
    };
  }, [dispatch]);

  return (
    <PageTemplate
      title="Companies"
      description="Manage all companies in the system"
      actions={
        <Button variant="primary" className={styles["header-create-company"]} onClick={handleCreateCompany}>
          <span className={styles["header-create-company-text"]}>Create Company</span>
          <span className={styles["header-create-company-icon"]}>
            <PlusIcon />
          </span>
        </Button>
      }
      className={styles.list}
    >
      <Card>
        {!isStatisticLoading && (
          <div className={styles.info}>
            {companiesInfo.map((item) => (
              <div className={styles.companiesInfoItem} key={item.title}>
                <p className={styles.companiesInfoItemTitle}>{item.title}</p>
                <p className={styles.companiesInfoItemValue}>{item.value}</p>
              </div>
            ))}
          </div>
        )}

        <SearchInput
          name="search"
          value={search}
          onChange={handleSearchChange}
          className={styles["search-input"]}
          placeholder="Search companies..."
        />

        {tableData?.length === 0 && (
          <div className={styles["not-found"]}>
            <NotFoundIcon />

            <p>{!statistics?.total_companies ? "No companies yet" : "No companies found"}</p>

            {!statistics?.total_companies && (
              <Button variant="secondary" onClick={handleCreateCompany}>
                Add first company
              </Button>
            )}
          </div>
        )}

        {!!tableData && tableData?.length > 0 && (
          <div className={styles["table-scroll"]}>
            <Table
              isLoading={isLoading}
              headers={tableHeaders}
              data={tableData}
              pagination={{
                total: meta.total_pages,
                page: meta.page,
                onPageChange: handlePageChange,
              }}
              className={styles.table}
            />
          </div>
        )}
      </Card>
    </PageTemplate>
  );
};

export default CompanyListPage;
